
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Gender = 'male' | 'female';
type Formula = 'mifflin' | 'harris' | 'katch';
type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very-active';

const BMRCalculator = () => {
  const [age, setAge] = useState<string>('30');
  const [weight, setWeight] = useState<string>('70');
  const [height, setHeight] = useState<string>('170');
  const [gender, setGender] = useState<Gender>('male');
  const [formula, setFormula] = useState<Formula>('mifflin');
  const [bodyFat, setBodyFat] = useState<string>('');
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate');
  
  const [bmr, setBmr] = useState<number | null>(null);
  const [tdee, setTdee] = useState<number | null>(null);

  const calculateMifflinStJeor = (): number => {
    const ageNum = parseInt(age);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    
    if (gender === 'male') {
      return (10 * weightNum) + (6.25 * heightNum) - (5 * ageNum) + 5;
    } else {
      return (10 * weightNum) + (6.25 * heightNum) - (5 * ageNum) - 161;
    }
  };
  
  const calculateHarrisBenedict = (): number => {
    const ageNum = parseInt(age);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    
    if (gender === 'male') {
      return 88.362 + (13.397 * weightNum) + (4.799 * heightNum) - (5.677 * ageNum);
    } else {
      return 447.593 + (9.247 * weightNum) + (3.098 * heightNum) - (4.330 * ageNum);
    }
  };
  
  const calculateKatchMcArdle = (): number => {
    const weightNum = parseFloat(weight);
    const bodyFatNum = parseFloat(bodyFat);
    
    if (!bodyFat) {
      toast.error("Body fat percentage is required for the Katch-McArdle formula");
      return 0;
    }
    
    const leanBodyMass = weightNum * (1 - (bodyFatNum / 100));
    return 370 + (21.6 * leanBodyMass);
  };

  const calculateTDEE = (baseBmr: number): number => {
    const activityMultipliers: Record<ActivityLevel, number> = {
      'sedentary': 1.2,
      'light': 1.375,
      'moderate': 1.55,
      'active': 1.725,
      'very-active': 1.9
    };
    
    return baseBmr * activityMultipliers[activityLevel];
  };

  const handleCalculate = () => {
    try {
      if (!age || !weight || !height) {
        toast.error("Please fill in all required fields");
        return;
      }
      
      if (formula === 'katch' && !bodyFat) {
        toast.error("Body fat percentage is required for the Katch-McArdle formula");
        return;
      }
      
      let calculatedBMR: number;
      
      switch (formula) {
        case 'mifflin':
          calculatedBMR = calculateMifflinStJeor();
          break;
        case 'harris':
          calculatedBMR = calculateHarrisBenedict();
          break;
        case 'katch':
          calculatedBMR = calculateKatchMcArdle();
          break;
        default:
          calculatedBMR = 0;
      }
      
      const calculatedTDEE = calculateTDEE(calculatedBMR);
      
      setBmr(Math.round(calculatedBMR));
      setTdee(Math.round(calculatedTDEE));
      
      toast.success("BMR calculated successfully");
    } catch (error) {
      toast.error("Error calculating BMR");
      console.error(error);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Basal Metabolic Rate (BMR) Calculator</CardTitle>
          <CardDescription>
            Calculate your Basal Metabolic Rate (BMR) and Total Daily Energy Expenditure (TDEE)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="mifflin" onValueChange={(value) => setFormula(value as Formula)}>
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="mifflin">Mifflin-St Jeor</TabsTrigger>
              <TabsTrigger value="harris">Harris-Benedict</TabsTrigger>
              <TabsTrigger value="katch">Katch-McArdle</TabsTrigger>
            </TabsList>
            
            <div className="mb-6">
              <Label>Gender</Label>
              <RadioGroup
                value={gender}
                onValueChange={(value: string) => setGender(value as Gender)}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="bmr-male" />
                  <Label htmlFor="bmr-male">Male</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="bmr-female" />
                  <Label htmlFor="bmr-female">Female</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="age">Age (years)</Label>
                  <Input
                    id="age"
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="mt-1"
                    min="15"
                    max="100"
                  />
                </div>
                
                <div>
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="mt-1"
                    min="100"
                    max="250"
                  />
                </div>
                
                <div>
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="mt-1"
                    min="30"
                    max="300"
                  />
                </div>
                
                {formula === 'katch' && (
                  <div>
                    <Label htmlFor="bodyFat">Body Fat (%)</Label>
                    <Input
                      id="bodyFat"
                      type="number"
                      value={bodyFat}
                      onChange={(e) => setBodyFat(e.target.value)}
                      className="mt-1"
                      min="2"
                      max="60"
                    />
                  </div>
                )}
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label htmlFor="activity">Activity Level</Label>
                  <Select
                    value={activityLevel}
                    onValueChange={(value: string) => setActivityLevel(value as ActivityLevel)}
                  >
                    <SelectTrigger id="activity" className="mt-1">
                      <SelectValue placeholder="Select activity level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedentary">Sedentary (little or no exercise)</SelectItem>
                      <SelectItem value="light">Lightly active (light exercise 1-3 days/week)</SelectItem>
                      <SelectItem value="moderate">Moderately active (moderate exercise 3-5 days/week)</SelectItem>
                      <SelectItem value="active">Active (hard exercise 6-7 days/week)</SelectItem>
                      <SelectItem value="very-active">Very active (very hard exercise & physical job)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="pt-8">
                  <TabsContent value="mifflin">
                    <p className="text-sm text-muted-foreground">
                      The Mifflin-St Jeor equation is considered the most accurate formula for estimating BMR in most people. It was published in 1990 and has been shown to be more accurate than the Harris-Benedict equation.
                    </p>
                  </TabsContent>
                  
                  <TabsContent value="harris">
                    <p className="text-sm text-muted-foreground">
                      The Harris-Benedict equation was published in 1919 and revised in 1984. It's one of the earliest and most widely used BMR formulas, though it's considered less accurate than newer formulas for some populations.
                    </p>
                  </TabsContent>
                  
                  <TabsContent value="katch">
                    <p className="text-sm text-muted-foreground">
                      The Katch-McArdle formula takes into account lean body mass, which makes it more accurate for athletic individuals with lower body fat percentages.
                    </p>
                  </TabsContent>
                </div>
                
                <Button 
                  onClick={handleCalculate} 
                  className="w-full mt-6"
                >
                  Calculate BMR
                </Button>
              </div>
            </div>
          </Tabs>

          {bmr !== null && tdee !== null && (
            <div className="mt-8 p-6 border rounded-lg bg-blue-50">
              <h3 className="text-xl font-semibold mb-4">Your Results</h3>
              
              <div className="mb-6">
                <div className="flex justify-between mb-1">
                  <span className="text-lg">BMR:</span>
                  <span className="font-bold text-primary text-lg">{bmr} calories/day</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  This is the number of calories your body needs to maintain basic functions at rest.
                </p>
              </div>
              
              <div className="mb-6">
                <div className="flex justify-between mb-1">
                  <span className="text-lg">TDEE:</span>
                  <span className="font-bold text-primary text-lg">{tdee} calories/day</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  This is your total daily energy expenditure, including your activity level.
                </p>
              </div>
              
              <h4 className="font-semibold mb-2">Calorie Goals</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Goal</TableHead>
                    <TableHead className="text-right">Daily Calories</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Weight Loss</TableCell>
                    <TableCell className="text-right">{Math.round(tdee * 0.8)} calories</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Maintenance</TableCell>
                    <TableCell className="text-right">{tdee} calories</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Weight Gain</TableCell>
                    <TableCell className="text-right">{Math.round(tdee * 1.15)} calories</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BMRCalculator;
